import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { apiEndpoint, apiRequestPath } from './api-endpoint.mjs';

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = apiEndpoint(BASE);

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

let csrfRequest;
// Read the current established store on every use; do not cache a bearer.
export function getSessionToken() {
  try {
    const token = localStorage.getItem('ca_token');
    return typeof token === 'string' && token ? token : null;
  } catch { return null; }
}

function webCsrfToken() {
  // Share concurrent bootstraps, but do not cache across writes: another tab,
  // login or MFA may have rotated the HttpOnly session cookie in the meantime.
  if (!csrfRequest) {
    csrfRequest = axios.get(`${API}/auth/csrf`, { withCredentials: true, timeout: 15000 })
      .then(({ data }) => {
        if (typeof data?.csrf_token !== 'string' || !data.csrf_token || data.csrf_token.length > 256) {
          throw new Error('No se pudo verificar la solicitud. Intenta de nuevo.');
        }
        return data.csrf_token;
      }).finally(() => { csrfRequest = undefined; });
  }
  return csrfRequest;
}

api.interceptors.request.use(async (config) => {
  config.url = apiRequestPath(config.url);
  const token = getSessionToken();
  const native = Capacitor.isNativePlatform();
  if (native) config.headers['X-CA-Native-App'] = '1';
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (!native && !['get', 'head', 'options'].includes((config.method || 'get').toLowerCase())) config.headers['X-CA-CSRF'] = await webCsrfToken();
  const nonce = sessionStorage.getItem('ca_profile_test_nonce');
  if (nonce) config.headers['X-CA-Test-Nonce'] = nonce;
  return config;
});

api.interceptors.response.use(r => r, (err) => {
  if (err.response?.status === 403 && err.response?.data?.detail === 'MFA_REQUIRED' && window.location.pathname !== '/verify-access') window.location.assign('/verify-access');
  if (err.response?.status === 403 && err.response?.data?.detail === 'PASSWORD_CHANGE_REQUIRED'
      && window.location.pathname !== '/change-password') {
    window.location.assign('/change-password');
  }
  return Promise.reject(err);
});

export function formatError(detail) {
  if (detail == null) return 'Ocurrió un error. Intenta de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Mensaje diagnóstico legible: distingue error HTTP del servidor vs. fallo de red/WebView vs. excepción local.
export function describeError(err, fallback = 'Ocurrió un error') {
  if (err?.response) return `${fallback} · HTTP ${err.response.status}: ${formatError(err.response.data?.detail)}`;
  if (err?.request) return `${fallback} · sin respuesta del servidor (${err.code || err.message || 'red'}) · ${API}`;
  return `${fallback} · ${err?.message || String(err)}`;
}

export const BACKEND_HOST = (() => { try { return new URL(API, window.location.origin).host; } catch { return ''; } })();
export const BUILD_SHA = (process.env.REACT_APP_BUILD_SHA || '').slice(0, 7) || 'dev';

export default api;
